import React from 'react';
import classnames from 'classnames';
import styled from '@emotion/styled';
import { PaymentMethodElement, PaymentMethodId } from '@atlassian/commerce-payment-methods';
import { Radio } from '@atlaskit/radio';
import Tooltip from '@atlaskit/tooltip';
import Avatar from '@atlaskit/avatar';
import { ValueType } from '@atlaskit/select';
import Lozenge from '@atlaskit/lozenge';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import { CopyableText, AchPanel } from '@css-xp/base';
import { TermPaymentMethod } from '@css-xp/base/term-payment-method';
import { AddContactSelectFields } from '@css-xp/ppc-add-contact-fields';
import { Y200, N300 } from '@atlaskit/theme/colors';
import { CustomerContactsAvatarGroup } from '@css-xp/ppc-subscription-table/src/components/customer-contacts-avatar-group';
// @ts-ignore
import UserIcon from '@css-xp/base/assets/img/application/icon-user.svg';
import { NoData } from '@css-xp/ppc-common';
import { splitAndCapitalize, isAchPaymentMethod } from '@css-xp/ppc-utils';
import {
  InvoiceGroup,
  CreditCard,
  PaymentMethodType,
  PaymentOnTerms,
  PaymentMethod,
  ContactInfo,
  PaymentMethodTypeV2,
  EndCustomerContact,
  Ach,
} from '@css-xp/graphtypes/src/generated-types-new';
import { PaypalPaymentMethod } from '@css-xp/ppc-paypal';
import { BillingProfileOption } from './order-summary';
import './billing-profile-panel.scss';

interface Props {
  billingProfile: InvoiceGroup | null | undefined;
  selectedBillingProfile: ValueType<BillingProfileOption> | null;
  setSelectedBillingProfile: (selected: ValueType<BillingProfileOption> | null) => void;
  isDisabled?: boolean;
  showBillingProfileId?: boolean;
  showPricingEligibility?: boolean;
  disableReason?: string;
  showPrimaryContacts?: boolean;
  primaryCustomerContact?: ContactInfo;
  isMultiCurrencyChangesEnabled?: boolean;
  atlassianAccountIds?: string[];
  atlassianAccountEmails?: string[];
  setAtlassianAccountIds?: React.Dispatch<React.SetStateAction<string[]>>;
  setAtlassianAccountEmails?: React.Dispatch<React.SetStateAction<string[]>>;
  allPrimaryContacts?: ContactInfo[];
  secondaryCustomerContacts?: EndCustomerContact[];
  shouldAllowAddingContacts?: boolean;
  shouldShowACHPaymentMethod?: boolean;
}

const PrimaryContactWarning = styled.div`
  display: flex;
  padding-top: 16px;
  .warning-text {
    color: ${N300};
    padding-left: 4px;
  }
`;

const StyledPrimaryContacts = styled.span`
  margin-left: 8px;
`;

export const BillingProfilePanel = ({
  billingProfile,
  selectedBillingProfile,
  setSelectedBillingProfile,
  isDisabled = false,
  showBillingProfileId = false,
  showPricingEligibility = false,
  disableReason,
  showPrimaryContacts = false,
  primaryCustomerContact,
  isMultiCurrencyChangesEnabled = false,
  atlassianAccountIds,
  atlassianAccountEmails,
  setAtlassianAccountEmails,
  setAtlassianAccountIds,
  allPrimaryContacts,
  secondaryCustomerContacts,
  shouldAllowAddingContacts = true,
  shouldShowACHPaymentMethod = false,
}: Props) => {
  const [hasExistingPrimaryContact, setHasExistingPrimaryContact] = React.useState<boolean>(true);

  const getDefaultPaymentSection = (defaultPaymentMethod?: PaymentMethod | null) => {
    if (!defaultPaymentMethod) {
      return <p>No payment method found</p>;
    }

    if (defaultPaymentMethod?.type === PaymentMethodType?.CARD) {
      const defaultPaymentMethodObj = defaultPaymentMethod as CreditCard;

      return (
        <PaymentMethodElement
          paymentMethod={{
            id: defaultPaymentMethodObj?.id as PaymentMethodId,
            type: defaultPaymentMethodObj?.type,
            // Because the 'card' prop should be used with PaymentMethodType.CARD type
            ...(defaultPaymentMethodObj?.type === PaymentMethodType.CARD && {
              card: {
                brand: defaultPaymentMethodObj?.card?.brand ?? '',
                expMonth: +(defaultPaymentMethodObj?.card?.expirationMonth ?? 0),
                expYear: +(defaultPaymentMethodObj?.card?.expirationYear ?? 0),
                last4: defaultPaymentMethodObj?.card?.last4Numbers ?? '',
                name: defaultPaymentMethodObj?.card?.cardHolderName ?? '',
              },
            }),
            default: defaultPaymentMethodObj?.default ?? false,
          }}
        />
      );
    }

    // In Order summary, there isn't any billing profile with associated payment method as push payments
    /* istanbul ignore next */
    if (defaultPaymentMethod?.type === PaymentMethodType?.DEFERRED) {
      const defaultPaymentMethodObj = defaultPaymentMethod as PaymentOnTerms;
      const hasPayByStatement = defaultPaymentMethodObj?.terms?.payOnStatement;
      const invoiceDueDays = defaultPaymentMethodObj?.terms?.invoiceDueDays;
      const termPaymentMethod = defaultPaymentMethodObj?.paymentMethod;
      const termPaymentType = invoiceDueDays ? `NET${invoiceDueDays}` : termPaymentMethod;
      const totalCreditLimit = defaultPaymentMethodObj?.totalCreditLimit;

      return (
        <TermPaymentMethod
          paymentNetType={hasPayByStatement ? '' : (termPaymentType as string)}
          totalLimit={hasPayByStatement ? undefined : (totalCreditLimit as number)}
          currentTermAccountStatus={billingProfile?.termPaymentStatus?.status}
          hasPayOnAccount={!!hasPayByStatement}
        />
      );
    }

    // @ts-ignore
    if (defaultPaymentMethod?.type === PaymentMethodTypeV2.PAYPAL) {
      const defaultPaymentMethodObj = defaultPaymentMethod as any;
      const emailID = defaultPaymentMethodObj?.payPal?.email;
      return <PaypalPaymentMethod emailID={emailID} />;
    }

    if (shouldShowACHPaymentMethod && isAchPaymentMethod(defaultPaymentMethod?.type)) {
      const defaultPaymentMethodObj = defaultPaymentMethod as Ach;
      return (
        <AchPanel
          bankName={defaultPaymentMethodObj?.ach?.bankName}
          last4={defaultPaymentMethodObj?.ach?.last4}
          showInfoInline={false}
          showBorder
        />
      );
    }
  };

  return (
    <div>
      <div className="billing-profile-panel-container">
        <Radio
          name={billingProfile?.name as string}
          id={billingProfile?.id as string}
          value={billingProfile?.id as string}
          isChecked={selectedBillingProfile?.value === billingProfile?.id}
          onChange={() => {
            const selected = {
              label: billingProfile?.name || '',
              value: billingProfile?.id || '',
            };
            setSelectedBillingProfile(selected);
            setHasExistingPrimaryContact(true);

            const primaryContact = allPrimaryContacts?.find((contact) => contact?.identifierId === selected?.value);
            if (!primaryContact && showPrimaryContacts) {
              setHasExistingPrimaryContact(false);
              setAtlassianAccountIds && setAtlassianAccountIds(['']);
              setAtlassianAccountEmails && setAtlassianAccountEmails(['']);
            } else {
              setHasExistingPrimaryContact(true);
            }
          }}
          isDisabled={isDisabled}
          testId="billing-profile-radio"
        />
        <div className="billing-profile-info-panel">
          <div className={classnames('billing-profile-panel-container-info', { disabled: isDisabled })}>
            <label
              data-test-id={`billing-profile-panel-container-info-label-${billingProfile?.id}`}
              data-testid={`billing-profile-panel-container-info-label-${billingProfile?.id}`}
              className="billing-profile-panel-container-info-label"
            >
              <span>{billingProfile?.name}</span>
              {isDisabled ? (
                <span className="ml-8 not-available-lozenge">
                  <Lozenge appearance="removed">Not available</Lozenge>
                </span>
              ) : null}
            </label>
            <div className="billing-profile-panel-container-info-details">
              <p>
                {billingProfile?.shipToParty?.name}, {billingProfile?.shipToParty?.addressLine1},{' '}
              </p>
              <p>
                {billingProfile?.shipToParty?.city}, {billingProfile?.shipToParty?.postcode}{' '}
                {billingProfile?.shipToParty?.state}, {billingProfile?.shipToParty?.country}
              </p>
              {showPricingEligibility && (
                <p>
                  Price eligibility:{' '}
                  {billingProfile?.shipToPriceEligibility ? (
                    splitAndCapitalize(billingProfile?.shipToPriceEligibility)
                  ) : (
                    <NoData />
                  )}
                </p>
              )}
              {isMultiCurrencyChangesEnabled && <p>Currency: {billingProfile?.currency ?? <NoData />}</p>}
            </div>
            <div className="billing-profile-panel-container-info-payment">
              {getDefaultPaymentSection(billingProfile?.defaultPaymentMethod)}
            </div>
            {showBillingProfileId && (
              <div className="billing-profile-panel-container-id">
                ID:&nbsp;
                {billingProfile?.id && (
                  <b>
                    <CopyableText text={billingProfile.id} />
                  </b>
                )}
              </div>
            )}
            {disableReason && (showPricingEligibility || isMultiCurrencyChangesEnabled) && (
              <div className="billing-profile-panel-container-disable-reason">{disableReason}</div>
            )}
          </div>
          {showPrimaryContacts && (
            <StyledPrimaryContacts>
              {primaryCustomerContact ? (
                <CustomerContactsAvatarGroup
                  contacts={[primaryCustomerContact] as any}
                  avatarSize="small"
                  showContactType={true}
                />
              ) : (
                <Tooltip content="Unassigned (Primary)">
                  <Avatar src={UserIcon.toString()} testId="unassigned-primary-contact" size="small" />
                </Tooltip>
              )}
            </StyledPrimaryContacts>
          )}
        </div>
      </div>
      <div>
        {shouldAllowAddingContacts &&
          showPrimaryContacts &&
          selectedBillingProfile?.value === billingProfile?.id &&
          !hasExistingPrimaryContact && (
            <>
              <PrimaryContactWarning>
                <WarningIcon label="" primaryColor={Y200} />
                <p className="warning-text">
                  This billing profile does not have a primary customer contact. Set a contact as primary to switch
                  billing profiles.
                </p>
              </PrimaryContactWarning>
              <AddContactSelectFields
                key={selectedBillingProfile?.value}
                isExistingContactRemoveDisabled={true}
                isAddNewContactDisabled={true}
                setAtlassianAccountIds={setAtlassianAccountIds as any}
                atlassianAccountIds={atlassianAccountIds as string[]}
                atlassianAccountEmails={atlassianAccountEmails as string[]}
                setAtlassianAccountEmails={setAtlassianAccountEmails as any}
                currentUserList={atlassianAccountIds}
                userAlreadyExistText="Customer contact already exists"
                emailFieldText="Select or add new contact"
                shouldAllowAddingContact={true}
                textFieldLabel="Enter email"
                nameFieldText="Enter contact full name"
                availableOptions={secondaryCustomerContacts?.map((contact: EndCustomerContact) => ({
                  fullName: contact?.name ?? '',
                  email: contact?.email ?? '',
                  picture: contact?.picture ?? '',
                  atlassianAccountId: contact?.accountId ?? '',
                }))}
                isRemoveButtonHidden
              />
            </>
          )}
      </div>
    </div>
  );
};
